import React, { useEffect } from 'react'

import { Button, Divider, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'

import { getLoginIdentityProvidersOptions, listMapsOptions, loginIdentityProviderOptions } from 'api/internal/generated/@tanstack/react-query.gen'
import type { _Map } from 'api/internal/generated/types.gen'
import PoziLogo from 'img/pozi.svg'
import { POZI_HOMEPAGE_URL } from 'utils/constants'

const loginButton = {
  justifyContent: 'center',
  textAlign: 'center' as const,
  textTransform: 'uppercase' as const,
  width: 200
}

const styles = {
  divider: { width: '500px' },
  loginButton,
  logo: {
    height: 150,
    paddingBottom: 12,
    paddingTop: 48
  },
  mapButton: { ...loginButton, width: 400 },
  pageContainer: { height: '60vh' }
}

const DEFAULT_PUBLIC_MAP = 'public'

const DEFAULT_AUTH_PROVIDER = 'cognito'
const ENTRA = 'entra'

type AuthProvider = typeof DEFAULT_AUTH_PROVIDER | typeof ENTRA

const AUTH_PROVIDERS = {
  DEFAULT_AUTH_PROVIDER,
  ENTRA
}

const LoginPage: React.FC = () => {
  const [availableMap, setAvailableMap] = React.useState<_Map | null>(null)
  const [identityProvider, setIdentityProvider] = React.useState('')
  const [isEntraConfigured, setIsEntraConfigured] = React.useState(false)
  const appInfo = window.getAppInfo()
  const site = appInfo.site || ''

  useEffect(() => {
    document.title = 'Sign In | Pozi'
  }, [])

  const currentHash = decodeURIComponent(window.location.hash)
  const mapPattern = /\/map\[([^\]]+)\]/
  const existingMapMatch = currentHash.match(mapPattern)
  const mapName = existingMapMatch ? existingMapMatch[1] : ''

  const getIdentityProviders = useQuery({
    ...getLoginIdentityProvidersOptions({
      headers: {
        'X-Pozi-Sitename': site
      }
    })
  })

  useEffect(() => {
    if (getIdentityProviders.isSuccess && !getIdentityProviders.isLoading) {
      const identityProviders = getIdentityProviders.data?.identity_providers
      const isIdentityProviderConfigured = identityProviders && identityProviders.length >= 1
      if (!isIdentityProviderConfigured) {
        // store.dispatch(setFatalError(true))
        return
      }
      setIsEntraConfigured(identityProviders.some((idp) => idp === 'entra') || false)
    }
  }, [getIdentityProviders.isLoading, getIdentityProviders.isSuccess, getIdentityProviders.data])

  const getLoginRedirect = useQuery({
    ...loginIdentityProviderOptions({
      headers: {
        'X-Pozi-Sitename': site,
        'X-Pozi-Mapname': mapName
      },
      path: {
        identity_provider: identityProvider as AuthProvider
      }
    }),
    enabled: !!identityProvider
  })

  useEffect(() => {
    if (getLoginRedirect.isSuccess && !getLoginRedirect.isLoading && getLoginRedirect.data?.login_url) {
      window.location.replace(getLoginRedirect.data.login_url)
    }
  }, [getLoginRedirect.isLoading, getLoginRedirect.isSuccess, getLoginRedirect.data])

  const getAvailableMaps = useQuery({
    ...listMapsOptions({
      headers: {
        'X-Pozi-Sitename': site
      }
    })
  })

  useEffect(() => {
    if (getAvailableMaps.isSuccess && !getAvailableMaps.isLoading && getAvailableMaps.data?.maps) {
      // Set the default public map if it exists, otherwise set the first available map
      setAvailableMap(getAvailableMaps.data.maps.find((map) => map.name === DEFAULT_PUBLIC_MAP) || getAvailableMaps.data.maps[0])
    }
  }, [getAvailableMaps.isLoading, getAvailableMaps.isSuccess, getAvailableMaps.data?.maps])

  const handleLoginClick = (identityProvider: string) => {
    setIdentityProvider(identityProvider)
  }

  const handleAvailableMapClick = (map: _Map) => {
    // Replace the existing map parameter with the new map name
    const newHash = currentHash.replace(`/map[${mapName}]`, `/map[${map.name}]`)
    window.location.hash = newHash
    window.location.reload()
  }

  const handleGoToPozi = () => {
    window.location.href = POZI_HOMEPAGE_URL
  }

  const Logo = <img src={PoziLogo} alt="Pozi Logo" style={styles.logo} />

  return (
    <Stack direction="column" alignItems="center" spacing={6} sx={styles.pageContainer}>
      <Stack spacing={3} alignItems="center">
        {Logo}
        <Typography variant="h5">Restricted Map</Typography>
        <Typography variant="body1">This map is accessible to authenticated users only.</Typography>
        <Stack direction="column" spacing={2} alignItems="center">
          {isEntraConfigured && (
            <Button onClick={() => handleLoginClick(AUTH_PROVIDERS.ENTRA)} sx={styles.loginButton}>
              Login with Entra
            </Button>
          )}
          <Button onClick={() => handleLoginClick(AUTH_PROVIDERS.DEFAULT_AUTH_PROVIDER)} variant="outlined" sx={styles.loginButton}>
            Login with Email
          </Button>
        </Stack>
        <Divider sx={styles.divider} />
        {availableMap && (
          <Stack direction="column" spacing={3} alignItems="center">
            <Typography variant="body1">If you are not a member of this organisation, you can view a public map here.</Typography>
            <Button key={availableMap.name} onClick={() => handleAvailableMapClick(availableMap)} sx={styles.mapButton}>
              {availableMap.title}
            </Button>
          </Stack>
        )}
        {!availableMap && (
          <Button onClick={handleGoToPozi} sx={styles.loginButton}>
            Go to Pozi.com
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

export default LoginPage
